import React, { useEffect, useState } from 'react';
import Icon from 'components/styleguide/icon/Icon';
import LoadingSpinner from 'components/styleguide/loading_spinner/LoadingSpinner';

const styles = require('./AiLoadingModal.module.scss');

interface AiLoadingModalProps {
  imageUrl: string;
  steps?: string[];
  isOpen?: boolean;
  triggerId?: string;
  context?: 'talent' | 'company';
}

const DEFAULT_STEPS = ['General information', 'Technical skills', 'Experience & Education'];
const MIN_STEP_DELAY_MS = 2000;
const MAX_EXTRA_DELAY_MS = 2000;

export default function AiLoadingModal({
  imageUrl,
  steps = DEFAULT_STEPS,
  isOpen: controlledOpen,
  triggerId,
  context = 'company',
}: AiLoadingModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const isOpen = controlledOpen ?? internalOpen;

  useEffect(() => {
    const img = new Image();
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    if (!triggerId) return;

    const trigger = document.getElementById(triggerId);
    if (!trigger) return;

    const handleOpen = () => setInternalOpen(true);
    trigger.addEventListener('click', handleOpen);
    return () => trigger.removeEventListener('click', handleOpen);
  }, [triggerId]);

  useEffect(() => {
    if (isOpen) setActiveStep(0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (activeStep >= steps.length - 1) return undefined;

    const delay = MIN_STEP_DELAY_MS + Math.random() * MAX_EXTRA_DELAY_MS;
    const timer = setTimeout(() => setActiveStep((prev) => prev + 1), delay);
    return () => clearTimeout(timer);
  }, [isOpen, activeStep, steps.length]);

  if (!isOpen) return null;

  return (
    <div className={styles.backdrop}>
      <div className={styles.modal}>
        <img src={imageUrl} alt="" className={styles.illustration} />
        <h2 className={styles.title}>Preparing your profile...</h2>
        <ul className={styles.steps}>
          {steps.map((step, index) => (
            <li key={step} className={styles.step}>
              <span className={styles.stepIcon}>
                {index < activeStep && <Icon name="circleCheck" color={context} size="md" />}
                {index === activeStep && <LoadingSpinner size="small-medium" context={context} />}
                {index > activeStep && <span className={styles.pendingCircle} aria-hidden="true" />}
              </span>
              <span className={styles.stepLabel}>{step}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
